import { Box, Chip, Grid, Paper, Typography, useMediaQuery, useTheme } from '@mui/material'
import { AccountBalanceWallet } from '@mui/icons-material'
import { PieChart } from '@mui/x-charts/PieChart'
import { useMemo, useState } from 'react'
import { SectorHeater, Stock } from '../_types/sector-heaters.type'
import { Formatters } from '../_helpers/formatter.helper'
import HeaterCard from './heater-card'
import HeaterCardLoading from './loading/heater-card.loading'

const PIE_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#3F51B5', '#8BC34A', '#FFC107',
  '#607D8B', '#795548', '#009688', '#CDDC39', '#F44336',
  '#673AB7', '#03A9F4', '#FF6F00', '#1B5E20', '#AD1457',
  '#0D47A1', '#E65100', '#4A148C', '#006064', '#BF360C',
  '#1A237E'
]

interface HeaterGridViewProps {
  heaters: SectorHeater[]
  loading: boolean
}

type SortOption = 'A-Z' | 'Gain High' | 'Gain Low'

interface FlattenedStock extends Stock {
  sectorTitle: string
}

export default function HeaterGridView({ heaters, loading }: HeaterGridViewProps) {
  const [sortBy, setSortBy] = useState<SortOption>('Gain High')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const flattenedStocks = useMemo(() => {
    const stocks: FlattenedStock[] = []
    heaters.forEach((heater) => {
      heater.stocks.forEach((stock) => {
        stocks.push({
          ...stock,
          sectorTitle: heater.title
        })
      })
    })
    return stocks
  }, [heaters])

  const sortedStocks = useMemo(() => {
    const sorted = [...flattenedStocks]

    switch (sortBy) {
      case 'A-Z':
        return sorted.sort((a, b) => a.symbol.localeCompare(b.symbol))
      case 'Gain High':
        return sorted.sort((a, b) => {
          const gainA = a.currentPX && a.entryPX
            ? ((a.currentPX - a.entryPX) / a.entryPX) * 100
            : 0
          const gainB = b.currentPX && b.entryPX
            ? ((b.currentPX - b.entryPX) / b.entryPX) * 100
            : 0
          return gainB - gainA
        })
      case 'Gain Low':
        return sorted.sort((a, b) => {
          const gainA = a.currentPX && a.entryPX
            ? ((a.currentPX - a.entryPX) / a.entryPX) * 100
            : 0
          const gainB = b.currentPX && b.entryPX
            ? ((b.currentPX - b.entryPX) / b.entryPX) * 100
            : 0
          return gainA - gainB
        })
      default:
        return sorted
    }
  }, [flattenedStocks, sortBy])

  const sortOptions: SortOption[] = ['A-Z', 'Gain High', 'Gain Low']

  const cashAmount = useMemo(() => {
    return heaters.reduce((sum, h) => sum + (h.cashAmount ?? 0), 0)
  }, [heaters])

  const portfolioData = useMemo(() => {
    const allStocks = heaters.flatMap(h => h.stocks)
    const rawSlices = allStocks
      .filter(s => s.shares && s.shares > 0 && s.currentPX)
      .map((s, i) => ({
        id: i,
        value: s.shares! * (s.currentPX ?? 0),
        ticker: s.symbol,
        color: PIE_COLORS[i % PIE_COLORS.length]
      }))

    const totalStockValue = rawSlices.reduce((sum, s) => sum + s.value, 0)
    const totalPortfolio = totalStockValue + cashAmount

    const stockSlices = rawSlices.map(s => ({
      ...s,
      label: `${s.ticker} (${((s.value / totalPortfolio) * 100).toFixed(1)}%)`
    }))

    const cashSlice = {
      id: stockSlices.length,
      value: cashAmount,
      ticker: 'Cash',
      label: `Cash (${((cashAmount / totalPortfolio) * 100).toFixed(1)}%)`,
      color: '#78909C'
    }

    return {
      slices: [...stockSlices, cashSlice],
      totalPortfolio,
      totalStockValue
    }
  }, [heaters, cashAmount])

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[...Array(6)].map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <HeaterCardLoading />
          </Grid>
        ))}
      </Grid>
    )
  }

  return (
    <Box>
      {/* Sorting Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Sort by:
        </Typography>
        {sortOptions.map((option) => (
          <Chip
            key={option}
            label={option}
            size="small"
            variant={sortBy === option ? 'filled' : 'outlined'}
            color={sortBy === option ? 'primary' : 'default'}
            onClick={() => setSortBy(option)}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {sortedStocks.length} stocks
        </Typography>
      </Box>

      {/* Grid of Cards */}
      <Grid container spacing={2} alignItems="flex-start">
        {sortedStocks.map((stock) => (
          <Grid item xs={12} sm={6} md={4} key={`${stock.symbol}-${stock.sectorTitle}`}>
            <HeaterCard stock={stock} sectorTitle={stock.sectorTitle} />
          </Grid>
        ))}
      </Grid>

      {sortedStocks.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No stocks available
          </Typography>
        </Box>
      )}

      {/* Cash and Portfolio Section */}
      {heaters.length > 0 && (
        <>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mt: 4,
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              color: 'primary.contrastText',
              borderRadius: 2
            }}
          >
            <AccountBalanceWallet sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box>
              <Typography variant='body2' sx={{ opacity: 0.85, fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Cash on Hand
              </Typography>
              <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
                ${Formatters.formatMoney(cashAmount)}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant='body2' sx={{ opacity: 0.85, fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Total Portfolio
              </Typography>
              <Typography variant='h5' sx={{ fontWeight: 600 }}>
                ${Formatters.formatMoney(portfolioData.totalPortfolio)}
              </Typography>
            </Box>
          </Paper>

          <Typography variant='h5' sx={{ mb: 2 }}>Portfolio Allocation</Typography>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'center' : 'flex-start',
                gap: 3
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <PieChart
                  series={[
                    {
                      data: portfolioData.slices,
                      innerRadius: 40,
                      outerRadius: isMobile ? 120 : 150,
                      paddingAngle: 1,
                      cornerRadius: 3,
                      highlightScope: { fade: 'global', highlight: 'item' },
                      valueFormatter: (item) => {
                        const pct = ((item.value / portfolioData.totalPortfolio) * 100).toFixed(1)
                        return `$${Formatters.formatMoney(item.value)} (${pct}%)`
                      }
                    }
                  ]}
                  width={isMobile ? 320 : 700}
                  height={isMobile ? 500 : 420}
                  slotProps={{
                    legend: {
                      direction: 'column' as const,
                      position: {
                        vertical: isMobile ? 'bottom' as const : 'middle' as const,
                        horizontal: isMobile ? 'middle' as const : 'right' as const
                      },
                      itemMarkWidth: 10,
                      itemMarkHeight: 10,
                      markGap: 5,
                      itemGap: 4,
                      labelStyle: {
                        fontSize: 12,
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  )
}
